import { 
    Box, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    useTheme, 
    IconButton, 
    Stack, 
    Tooltip, 
    Typography, 
    useMediaQuery, 
    Grid, 
    TextField, 
    Select, 
    MenuItem, 
    InputLabel, 
    FormControl, 
    FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { useSelector } from "react-redux";
import { Form, FormikProvider, useFormik } from "formik";
import { toast } from "react-toastify";

import { GeofenceTypes } from "../../../types/geofence.types";
import { RootState } from "../../../store";
import { createGeoPhoneSchema } from "../../../schemas/geofence.schema";
import { GeofenceService } from "../../../services/geofence.service";
import { When } from "react-if";

interface Props {
    editGeofencePhone?: GeofenceTypes.GeofencePhoneNumber | null;
    visible: boolean;
    onClose: () => void;
}

export const GeofencePhoneForm: React.FC<Props> = ({
    visible,
    onClose,
    editGeofencePhone,
}) => {
    const { loading, geoPhones } = useSelector((state: RootState) => state.geofence);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const alreadyHaveOutsideLocation = geoPhones.filter(geo => geo.type === 'outside').length > 0;
    const alreadyHaveUnknownLocation = geoPhones.filter(geo => geo.type === 'unknown').length > 0;

    const formik = useFormik<GeofenceTypes.CreateGeoPhoneParams>({
        validationSchema: createGeoPhoneSchema,
        initialValues: editGeofencePhone ? {
            label: editGeofencePhone.label,
            phone: editGeofencePhone.phoneNumber,
            type: editGeofencePhone.type
        } as GeofenceTypes.CreateGeoPhoneParams : 
        {} as GeofenceTypes.CreateGeoPhoneParams,
        onSubmit: async (geofencePhone) => {
            if (!editGeofencePhone) {
                if (alreadyHaveOutsideLocation && geofencePhone.type === 'outside') {
                    onClose();

                    toast(
                        'You already have added a phone for outside geofence locations',
                        {
                            autoClose: 1500,
                            type: 'error',
                        }
                    );

                    return;
                }

                if (alreadyHaveUnknownLocation && geofencePhone.type === 'unknown') {
                    onClose();

                    toast(
                        'You already have added a phone for unknown locations',
                        {
                            autoClose: 1500,
                            type: 'error',
                        }
                    );

                    return;
                }

                await GeofenceService.createGeoPhone(geofencePhone, (isValid, msg) => {
                    if (!isValid) {
                        toast(
                            msg,
                            {
                                autoClose: 1500,
                                type: 'error',
                            }
                        );
                        return;
                    }
    
                    toast(
                        'Phone successfully created.',
                        {
                            autoClose: 1500,
                            type: 'success',
                        },
                    );
                });
            } else {
                await GeofenceService.updateGeoPhone({
                    id: editGeofencePhone.id,
                    label: geofencePhone.label,
                    phone: geofencePhone.phone,
                    type: geofencePhone.type,
                } as GeofenceTypes.UpdateGeoPhoneParams, (isValid, msg) => {
                    if (!isValid) {
                        toast(
                            msg,
                            {
                                autoClose: 1500,
                                type: 'error',
                            }
                        );
                        return;
                    }
    
                    toast(
                        'Phone successfully updated.',
                        {
                            autoClose: 1500,
                            type: 'success',
                        },
                    );
                });
            }

            resetForm();
            onClose();
        }
    });

    const { handleSubmit, errors, values, touched, resetForm, getFieldProps } = formik;

    return (
        <Dialog
            open={visible}
            fullWidth={isSmallScreen}
            maxWidth={'xl'}
            scroll="paper"
        >
            <Box sx={{ borderBottom: 1, p: 2, borderColor: 'divider' }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Typography fontWeight={400} fontSize={20} textAlign={'left'}>
                        {editGeofencePhone ? 'Edit phone' : 'Add a new phone'}
                    </Typography>
                    <Tooltip title="Close form">
                        <IconButton
                            onClick={onClose}
                            component="span"
                            sx={{ mr: 2, height: 30, width: 30, alignSelf: 'center' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <FormikProvider value={formik}>
                <Form noValidate onSubmit={handleSubmit}>
                    <DialogContent dividers sx={{ minWidth: isSmallScreen ? '90%' : 700 }}>
                        <Grid container spacing={2}>
                            <Grid item md={12} sm={24} xs={24}>
                                <TextField
                                    label="Label"
                                    placeholder="Please type a label for the phone number"
                                    fullWidth
                                    required
                                    variant='outlined'
                                    error={Boolean(errors.label && touched.label)}
                                    helperText={touched.label && errors.label}
                                    {...getFieldProps('label')}
                                />
                            </Grid>
                            <Grid item md={12} sm={24} xs={24}>
                                <TextField
                                    label="Phone"
                                    placeholder="Please type the phone number"
                                    fullWidth
                                    required
                                    variant='outlined'
                                    error={Boolean(errors.phone && touched.phone)}
                                    helperText={touched.phone && errors.phone}
                                    {...getFieldProps('phone')}
                                />
                            </Grid>
                            <Grid item md={12} sm={24} mt={2} xs={24}>
                                <FormControl fullWidth>
                                    <InputLabel id="geofence-phone-type">
                                        Phone Type
                                    </InputLabel>
                                    <Select
                                        disabled={Boolean(editGeofencePhone)}
                                        labelId="geofence-phone-type"
                                        label="Geofence Phone Type"
                                        required
                                        error={Boolean(errors.type && touched.type)}
                                        fullWidth
                                        {...getFieldProps('type')}
                                    >
                                        <MenuItem key={'outside'} value={'outside'}>
                                            Outside Geofence Location
                                        </MenuItem>

                                        <MenuItem key={'unknown'} value={'unknown'}>
                                            Unknown Location
                                        </MenuItem>
                                    </Select>
                                    {Boolean(touched.type && errors.type) && (
                                        <FormHelperText
                                            error
                                            style={{ color: '#ff0000' }}
                                        >
                                            {errors.type}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            disabled={
                                loading.createGeoPhone || 
                                loading.updateGeoPhone || 
                                Object.values(errors)?.length > 0 ||
                                !values.label ||
                                !values.phone ||
                                !values.type
                            }
                            fullWidth={isSmallScreen}
                            loading={loading.createGeoPhone || loading.updateGeoPhone}
                        >
                            Confirm
                        </LoadingButton>
                    </DialogActions>
                </Form>
            </FormikProvider>
        </Dialog>
    )
}